import React, { useState, useEffect, useMemo } from 'react';
import { Button, Row, Col, Form, Input } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { Tooltip } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

import FlexSelect from '../../../../../components/Inputs/FlexSelect';
import xlsxImage from '../../../../../assets/img/xlsx.png';
import WhatsappBlockListService from '../../../services';
import Loader from '../../../../../components/Loader';

import { buttonStyle } from '../../../../../components/Containers/ConfigurationContainer/components/utils';
import { resetFields } from '../../../../CRM/shared/helpers/resetInputsFIeldsValues';
import { ClearFiltersButton } from '../../../../../components/ClearFiltersButton';
import { useUsers } from '../../../../Report/Protocol/hooks/useUsers';
import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from '../../../../../hooks/useMemorizeInputsFilters';
import {
  ImageButton,
  ImageSize,
  ImageButtonConteinerProtocol
} from '../../../../../assets/styles/image_style';

const pageKey = 'flexTableConfig::[activeWhatsappBlock]';

const LIMIT_ROWS = 40000;

const ActiveWhatsappBlockListForm = ({
  handleSearch,
  totalWhatsappBlockNumbers,
  whatsappBlockNumbersList,
  setDataToSend,
  templates = []
}) => {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const { control, handleSubmit, getValues, setValue, reset } = useForm();

  const [loadingCSV, setLoadingCSV] = useState(false);

  const { memorizeFilters, getMemorizedFilters, clearMemorizedFilters } =
    useMemorizeFilters(POSSIBLE_FILTERS_ENTITIES.WHATSAPP_BLOCK_LIST);

  const { userList } = useUsers();

  useEffect(() => {
    const filters = getMemorizedFilters();
    if (filters) {
      reset(filters);
      handleSearch(filters);
    }
  }, [getMemorizedFilters, reset, resetConfig, setValue]);

  const resetConfig = useMemo(
    () => ({
      telephone: () => '',
      userLogins: () => '',
      reason: () => '',
      templateIds: () => ''
    }),
    []
  );

  const onSubmit = async (data) => {
    memorizeFilters(data);
    const formattedData = { ...data };
    setDataToSend({ ...formattedData });
    await handleSearch({ ...formattedData });
  };

  const handleReportExport = (typeExport, e) => {
    e.preventDefault();

    const params = getValues([
      'telephone',
      'userLogins',
      'reason',
      'templateIds'
    ]);

    const columnConfig = JSON.parse(localStorage.getItem(pageKey)) || [];
    const orderByConfig = columnConfig.find((col) => col.isSort);
    const orderBy = orderByConfig ? formatColumnName(orderByConfig.key) : null;
    const orderType = orderByConfig
      ? orderByConfig.sortDirection === 'ascending'
        ? 'ASC'
        : 'DESC'
      : null;
    const hiddenColumns = (columnConfig || [])
      .filter((col) => !col.visible)
      .map((col) => formatColumnName(col.key));

    const columnsConfig = localStorage.getItem(pageKey);
    const columnsOrder = columnsConfig
      ? JSON.parse(columnsConfig).map((col) => formatColumnName(col.key))
      : [];

    const filteredColumnsOrder = columnsOrder.filter(
      (col) => col !== 'edit' && col !== 'remove' && col !== 'details'
    );

    const columnsOrderString = filteredColumnsOrder.join(',');
    const columnsOrderEncoded = encodeURIComponent(columnsOrderString);

    const dto = {
      ...params,
      orderBy,
      orderType,
      hiddenColumn: hiddenColumns,
      columnsOrder: columnsOrderEncoded
    };

    if (totalWhatsappBlockNumbers < LIMIT_ROWS && typeExport == 'XLSX') {
      setLoadingCSV(true);
      WhatsappBlockListService.getReportTypeXlsx(dto)
        .then((res) => {
          if (res.data.size === 0) {
            setLoadingCSV(false);
            toast.info('Nenhum registro foi encontrado!');
          } else {
            saveAs(
              new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
              }),
              `lista-bloqueio-whatsapp${new Date().toISOString()}.xlsx`
            );
            setLoadingCSV(false);
          }
        })
        .catch(() => {
          setLoadingCSV(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    } else if (totalWhatsappBlockNumbers > LIMIT_ROWS) {
      setLoadingCSV(true);
      WhatsappBlockListService.getReportTypeCSV(dto)
        .then((res) => {
          if (res.data.size === 0) {
            setLoadingCSV(false);
            toast.info('Nenhum registro foi encontrado!');
          } else {
            saveAs(
              new Blob([res.data], { type: 'text/csv;charset=utf-8;' }),
              `lista-bloqueio-whatsapp${new Date().toISOString()}.csv`
            );
            setLoadingCSV(false);
          }
        })
        .catch(() => {
          setLoadingCSV(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    }
  };

  const formatColumnName = (name) => {
    return name.replace(/([A-Z])/g, '_$1').toLowerCase();
  };

  const handleReset = () => {
    resetFields(resetConfig, [], control);
    clearMemorizedFilters();
  };

  return (
    <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
      <ClearFiltersButton
        styles={{ position: 'absolute', right: '1rem', top: '2rem' }}
        handleOnClick={handleReset}
      />
      <Row className="mb-3">
        <Col sm="6">
          <label className="form-control-label">Telefone</label>
          <Controller
            as={<Input type="text" name="telephone" />}
            control={control}
            name="telephone"
            defaultValue=""
          />
        </Col>
        <Col sm="6">
          <label className="form-control-label">Motivo</label>
          <Controller
            as={<Input type="text" name="reason" />}
            control={control}
            name="reason"
            defaultValue=""
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col sm="6">
          <label className="form-control-label">Template</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={templates || []}
                isMulti={true}
                value={props.value}
                valueController={setValue}
                fieldName="templateIds"
                labelName="name"
                valueName="id"
                isClearable={true}
                closeMenuOnSelect={false}
              />
            )}
            control={control}
            name="templateIds"
            defaultValue=""
          />
        </Col>
        <Col sm="6">
          <label className="form-control-label">Responsável</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={userList || []}
                isMulti={true}
                value={props.value}
                valueController={setValue}
                fieldName="userLogins"
                labelName="name"
                valueName="login"
                isClearable={true}
                closeMenuOnSelect={false}
              />
            )}
            control={control}
            name="userLogins"
            defaultValue=""
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="mt-3" style={ImageButtonConteinerProtocol}>
            <Button
              style={buttonStyle(systemConfiguration.primary_color)}
              type="submit"
              size="sm"
            >
              Buscar
            </Button>
          </div>
        </Col>
        <Col
          md="6"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
          }}
        >
          <button
            id="button_xlsx"
            style={ImageButton}
            onClick={(e) => {
              handleReportExport('XLSX', e);
            }}
            disabled={
              whatsappBlockNumbersList.length && !loadingCSV ? false : true
            }
          >
            <Tooltip
              title={
                <p style={{ fontSize: '14px' }}>
                  {whatsappBlockNumbersList.length
                    ? `Fazer download do relatório de Lista de números bloqueados do WhatsApp. Arquivos menores que ${LIMIT_ROWS} registros serão baixados em XLSX, caso contrário CSV`
                    : 'Nenhum registro para realizar download'}
                </p>
              }
              arrow
            >
              {loadingCSV ? (
                <Loader
                  type="ThreeDots"
                  visible={true}
                  height="40"
                  width="40"
                />
              ) : (
                <img src={xlsxImage} alt="xlsx" style={ImageSize} />
              )}
            </Tooltip>
          </button>
        </Col>
      </Row>
      <div>
        <div style={{ fontSize: 'small', float: 'right' }}>
          {totalWhatsappBlockNumbers} resultado(s) encontrado(s)
        </div>
      </div>
    </Form>
  );
};

export default ActiveWhatsappBlockListForm;
